// Fila de alertas do vigia da Vick.
//
// A leitura vai direto pela Data API: `ia_alertas` tem RLS que já filtra por
// módulo do perfil (`ia_alerta_visivel`), então não há o que esconder no
// cliente — o que chega aqui é o que a pessoa pode ver.
//
// Quem não tem `vick:<modulo>` (nem é adm) receberia lista vazia da RLS. Ainda
// assim o hook checa antes de sair: sem isso, quem não tem alerta nenhum paga
// uma ida ao banco e um canal de realtime a cada tela, e a aba "Alertas"
// aparece prometendo algo que nunca vai chegar.
//
// A BAIXA, ao contrário, não é UPDATE direto: passa pela RPC `baixar_ia_alerta`,
// que confere o módulo de novo e exige motivo para ignorar. Deixar o cliente
// escrever na tabela transformaria a RLS de leitura em teatro.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { podeVerAlertas } from '../lib/vickAlertas';
import { useAlertasLidos } from './useAlertasLidos';
import type { IaAlerta, IaAlertaEstado, IaAlertaModulo, IaAlertaSeveridade } from '../../types';

export interface FiltrosAlertas {
  estado: IaAlertaEstado;
  modulo: IaAlertaModulo | 'todos';
  severidade: IaAlertaSeveridade | 'todas';
}

export const FILTROS_PADRAO: FiltrosAlertas = {
  estado: 'aberto',
  modulo: 'todos',
  severidade: 'todas',
};

/** Ordem de gravidade, para ordenar a fila pelo que precisa de atenção antes. */
const PESO: Record<IaAlertaSeveridade, number> = { alta: 0, media: 1, baixa: 2 };

export const ROTULO_MODULO: Record<IaAlertaModulo, string> = {
  financeiro: 'Financeiro',
  rateio: 'Rateio e energia',
  comercial: 'Comercial',
  geradoras: 'Geradoras',
};

export const ROTULO_SEVERIDADE: Record<IaAlertaSeveridade, string> = {
  alta: 'Urgente',
  media: 'Atenção',
  baixa: 'Informativo',
};

export function useIaAlertas(filtros: FiltrosAlertas = FILTROS_PADRAO) {
  const [alertas, setAlertas] = useState<IaAlerta[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const { profile } = useAuth();
  const temAcesso = podeVerAlertas(profile);

  const { estado, modulo, severidade } = filtros;

  const carregar = useCallback(async () => {
    if (!temAcesso) {
      setAlertas([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      let q = supabase
        .from('ia_alertas')
        .select('*')
        .eq('estado', estado)
        .order('detectado_em', { ascending: false })
        .limit(200);
      if (modulo !== 'todos') q = q.eq('modulo', modulo);
      if (severidade !== 'todas') q = q.eq('severidade', severidade);

      const { data, error } = await q;
      if (error) throw error;
      setAlertas((data ?? []) as IaAlerta[]);
    } catch (e: any) {
      console.error('Erro ao carregar alertas da Vick:', e);
      setErro(e?.message || 'Não foi possível carregar os alertas.');
    } finally {
      setLoading(false);
    }
  }, [estado, modulo, severidade, temAcesso]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const ordenados = useMemo(
    () =>
      [...alertas].sort(
        (a, b) =>
          PESO[a.severidade] - PESO[b.severidade] ||
          Date.parse(b.detectado_em) - Date.parse(a.detectado_em),
      ),
    [alertas],
  );

  /**
   * Marca um alerta como resolvido ou ignorado.
   *
   * A RPC devolve erro falante (sem permissão, já baixado, ignorar sem motivo).
   * Repassamos a mensagem em vez de um "erro ao salvar" genérico — quem está
   * triando precisa saber qual das três coisas aconteceu.
   */
  const baixar = useCallback(
    async (id: number, novoEstado: 'resolvido' | 'ignorado', motivo?: string): Promise<string | null> => {
      const { error } = await supabase.rpc('baixar_ia_alerta', {
        p_id: id,
        p_estado: novoEstado,
        p_motivo: motivo ?? null,
      });
      if (error) {
        console.error('Erro ao baixar alerta:', error);
        return error.message || 'Não foi possível baixar o alerta.';
      }
      setAlertas((prev) => prev.filter((a) => a.id !== id));
      return null;
    },
    [],
  );

  return { alertas: ordenados, loading, erro, recarregar: carregar, baixar, temAcesso };
}

/**
 * Quantos alertas abertos esta pessoa ainda NÃO leu — o contador do botão
 * flutuante.
 *
 * É o não lido, e não o tamanho da fila, de propósito: badge é chamado de
 * atenção. O alerta agregado de saldo fica aberto por semanas, e uma bolinha
 * vermelha que nunca zera vira parte do cenário — para de ser lida. Quem quer
 * saber o tamanho da fila abre a aba Alertas, onde o número é informação e não
 * alarme.
 */
export function useContagemAlertasNaoLidos(): number {
  const [naoLidos, setNaoLidos] = useState(0);
  const { profile } = useAuth();
  const { lidosAte } = useAlertasLidos();
  const temAcesso = podeVerAlertas(profile);

  useEffect(() => {
    if (!temAcesso) {
      setNaoLidos(0);
      return;
    }
    let vivo = true;
    const contar = async () => {
      // Conta no servidor (`head: true` não traz linha nenhuma) em vez de
      // baixar a fila e filtrar em memória — a fila tem teto de página e uma
      // contagem que depende dele começa a mentir sem avisar.
      let q = supabase
        .from('ia_alertas')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'aberto');
      // Nunca leu nada: todo alerta aberto é novo.
      if (lidosAte > 0) q = q.gt('detectado_em', new Date(lidosAte).toISOString());

      const { count } = await q;
      if (vivo) setNaoLidos(count ?? 0);
    };
    contar();

    // O vigia roda de hora em hora; o realtime evita a pessoa ter que recarregar
    // a página para ver que algo apareceu.
    const canal = supabase
      .channel('ia_alertas_contagem')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ia_alertas' }, contar)
      .subscribe();

    return () => {
      vivo = false;
      supabase.removeChannel(canal);
    };
  }, [temAcesso, lidosAte]);

  return naoLidos;
}
